import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyLink, Note, Stack, Textarea } from '@src/components';

export interface InviteMessageFormProps {
  /** Draft message — `<link>` is replaced with the real URL when copied. */
  message: string;
  onMessageChange: (message: string) => void;
  messageLabel: string;
  /** Explains the `<link>` placeholder under the message field. */
  messageHint?: string;
  /** Read-only: the organizer copies it, they do not type it. */
  link: string;
  linkLabel: string;
  /** What copying this link does, and what it costs to open it yourself. */
  warning: string;
  testId?: string;
  onLinkCopied?: () => void;
}

/**
 * Body of the two copy-an-invite dialogs. Shared because a personal link and
 * the group link differ only in their labels and their warning — the shape of
 * what the organizer does (read it, edit the wording, copy) is the same.
 *
 * The draft lives here so typing does not have to re-open the host dialog.
 * Re-showing that snapshot remounts the textarea and parks the caret at the end.
 */
export function InviteMessageForm({
  message,
  onMessageChange,
  messageLabel,
  messageHint,
  link,
  linkLabel,
  warning,
  testId,
  onLinkCopied,
}: InviteMessageFormProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(message);

  return (
    <Stack gap={4} wide>
      <Textarea
        value={draft}
        onValueChange={(next) => {
          setDraft(next);
          onMessageChange(next);
        }}
        label={messageLabel}
        hint={messageHint}
        rows={10}
        autosize={false}
        testId={testId ? `${testId}-message` : undefined}
      />
      <CopyLink
        label={linkLabel}
        link={link}
        copyHint={t('box.invite.linkCopyHint')}
        copiedHint={t('box.invite.linkCopiedHint')}
        disabled={!link.trim()}
        onCopied={onLinkCopied}
        testId={testId ? `${testId}-link` : undefined}
      />
      <Note tone="info">
        {warning}
      </Note>
    </Stack>
  );
}
